import { useEffect, useMemo, useState } from 'react'
import { ChevronRight, ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils/cn'
import { usePermission } from '@/shell/module-system'
import type { HelpManifest, ManifestSection, ManifestItem } from '../api/queries'

const STORAGE_KEY = 'spi-ops:help-sidebar-expanded'
const DEV_GROUP_KEY = 'spi-ops:help-dev-section-collapsed'

export interface HelpSidebarProps {
  manifest: HelpManifest
  /** Currently-active page URL — used to highlight the matching item. */
  activeUrl?: string
}

/**
 * Recursive tree nav rendered from the help manifest.
 *
 * Audience filter (sprint-user-guides): non-admin operators see only
 * sections with `audience === 'user'` (the User guides surface).
 * Admins see those first, then a collapsible "Developer reference"
 * group containing every `audience === 'admin'` section (Modules,
 * Services, Workers, API, Models, Glossary, About) — collapsed by
 * default so admins land on user guides first too.
 *
 * Expand state for individual sections persists in localStorage
 * (key 'spi-ops:help-sidebar-expanded') so the operator's preferred
 * shape sticks across reloads. The dev-group collapse state lives
 * under 'spi-ops:help-dev-section-collapsed'.
 */
export function HelpSidebar({ manifest, activeUrl }: HelpSidebarProps) {
  const isAdmin = usePermission('admin')
  const [expandedSections, setExpandedSections] = useState<Set<string>>(() => {
    if (typeof window === 'undefined') return new Set(['modules'])
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY)
      if (raw) {
        const parsed = JSON.parse(raw) as { sections?: string[] }
        return new Set(parsed.sections ?? ['modules'])
      }
    } catch {
      /* ignore */
    }
    return new Set(['modules'])
  })

  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set())

  useEffect(() => {
    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ sections: Array.from(expandedSections) }),
      )
    } catch {
      /* ignore */
    }
  }, [expandedSections])

  // Tracks sections the user has explicitly closed since mount, even
  // if activeUrl would have auto-opened them. Without this, clicking
  // an auto-expanded section is a no-op (the auto-set ignores the
  // explicit-open removal).
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(
    () => new Set(),
  )

  // Sections that should be open because the active URL lives inside
  // them. Derived at render — never written into state — so user
  // toggles + activeUrl drive expansion independently without the
  // setState-in-effect cascade.
  const autoExpandedSections = useMemo(() => {
    const out = new Set<string>()
    if (!activeUrl) return out
    for (const section of manifest.sections) {
      for (const item of section.items) {
        if (activeUrl.startsWith(item.url)) out.add(section.id)
      }
    }
    return out
  }, [activeUrl, manifest.sections])

  const effectiveExpandedSections = useMemo(() => {
    const out = new Set<string>([...expandedSections, ...autoExpandedSections])
    for (const id of collapsedSections) out.delete(id)
    return out
  }, [expandedSections, autoExpandedSections, collapsedSections])

  function toggleSection(id: string) {
    const isOpen = effectiveExpandedSections.has(id) && !collapsedSections.has(id)
    if (isOpen) {
      setCollapsedSections((prev) => new Set(prev).add(id))
      setExpandedSections((prev) => {
        if (!prev.has(id)) return prev
        const next = new Set(prev)
        next.delete(id)
        return next
      })
    } else {
      setCollapsedSections((prev) => {
        if (!prev.has(id)) return prev
        const next = new Set(prev)
        next.delete(id)
        return next
      })
      setExpandedSections((prev) => new Set(prev).add(id))
    }
  }

  function toggleItem(url: string) {
    setExpandedItems((prev) => {
      const next = new Set(prev)
      if (next.has(url)) next.delete(url)
      else next.add(url)
      return next
    })
  }

  // Partition sections by audience. Non-admins never see admin sections.
  const userSections = useMemo(
    () => manifest.sections.filter((s) => s.audience === 'user'),
    [manifest.sections],
  )
  const adminSections = useMemo(
    () => (isAdmin ? manifest.sections.filter((s) => s.audience === 'admin') : []),
    [manifest.sections, isAdmin],
  )

  // Dev-group collapse state — admin-only and persisted separately.
  const [devGroupCollapsed, setDevGroupCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true
    try {
      const raw = window.localStorage.getItem(DEV_GROUP_KEY)
      return raw === null ? true : raw === '1'
    } catch {
      return true
    }
  })
  useEffect(() => {
    try {
      window.localStorage.setItem(DEV_GROUP_KEY, devGroupCollapsed ? '1' : '0')
    } catch {
      /* ignore */
    }
  }, [devGroupCollapsed])

  return (
    <nav aria-label="Help navigation" className="py-2">
      {userSections.map((section) => (
        <SectionNode
          key={section.id}
          section={section}
          expanded={effectiveExpandedSections.has(section.id)}
          onToggle={() => toggleSection(section.id)}
          activeUrl={activeUrl}
          expandedItems={expandedItems}
          onToggleItem={toggleItem}
        />
      ))}

      {adminSections.length > 0 ? (
        <div className="mt-4 border-t border-border-subtle pt-2">
          <button
            type="button"
            onClick={() => setDevGroupCollapsed((v) => !v)}
            className="flex w-full items-center gap-1 px-3 py-1 text-left text-role-section-label text-tertiary hover:bg-surface-hover"
            aria-expanded={!devGroupCollapsed}
          >
            {devGroupCollapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
            <span>Developer reference</span>
            <span className="ml-auto text-2xs">{adminSections.length}</span>
          </button>
          {!devGroupCollapsed
            ? adminSections.map((section) => (
                <SectionNode
                  key={section.id}
                  section={section}
                  expanded={effectiveExpandedSections.has(section.id)}
                  onToggle={() => toggleSection(section.id)}
                  activeUrl={activeUrl}
                  expandedItems={expandedItems}
                  onToggleItem={toggleItem}
                />
              ))
            : null}
        </div>
      ) : null}
    </nav>
  )
}


function SectionNode({
  section,
  expanded,
  onToggle,
  activeUrl,
  expandedItems,
  onToggleItem,
}: {
  section: ManifestSection
  expanded: boolean
  onToggle: () => void
  activeUrl?: string
  expandedItems: Set<string>
  onToggleItem: (url: string) => void
}) {
  return (
    <div className="mb-1">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center gap-1 px-3 py-1 text-left text-role-section-label hover:bg-surface-hover"
      >
        {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        <span>{section.label}</span>
        <span className="ml-auto text-2xs text-tertiary">{section.items.length}</span>
      </button>
      {expanded ? (
        <div className="flex flex-col">
          {section.items.map((item) => (
            <ItemNode
              key={item.url}
              item={item}
              isActive={activeUrl === item.url}
              isOpen={expandedItems.has(item.url)}
              onToggle={() => onToggleItem(item.url)}
            />
          ))}
        </div>
      ) : null}
    </div>
  )
}


function ItemNode({
  item,
  isActive,
  isOpen,
  onToggle,
}: {
  item: ManifestItem
  isActive: boolean
  isOpen: boolean
  onToggle: () => void
}) {
  const hasSubs = item.subsections.length > 0
  return (
    <div>
      <div
        className={cn(
          'group flex items-center gap-1 pl-7 pr-3 py-1 text-sm hover:bg-surface-hover',
          isActive && 'bg-accent-bg text-accent-text font-semibold',
        )}
      >
        {hasSubs ? (
          <button
            type="button"
            onClick={onToggle}
            aria-label={isOpen ? 'Collapse' : 'Expand'}
            className="flex h-4 w-4 cursor-pointer items-center justify-center text-tertiary opacity-60 hover:opacity-100"
          >
            {isOpen ? <ChevronDown size={10} /> : <ChevronRight size={10} />}
          </button>
        ) : (
          <span className="w-4" />
        )}
        <a
          href={item.url}
          className="flex-1 truncate text-primary no-underline hover:text-link"
        >
          {item.label}
        </a>
        {item.tool_count !== null && item.tool_count > 0 ? (
          <span className="text-2xs text-tertiary">{item.tool_count}</span>
        ) : null}
      </div>
      {isOpen && hasSubs ? (
        <div className="flex flex-col">
          {item.subsections.map((sub) => (
            <a
              key={sub.url}
              href={sub.url}
              className="truncate pl-12 pr-3 py-0.5 text-xs text-secondary hover:bg-surface-hover hover:text-link"
            >
              {sub.text}
            </a>
          ))}
        </div>
      ) : null}
    </div>
  )
}
